import React, { useState, useEffect } from 'react';
import './SettingsMenu.css'; // Ensure to create this CSS file
import SkipMarkForm from './SkipMarkForm';
import ApiService from '../../services/ApiService';
import ContentService from '../../services/ContentService';
import AnimixPremiumPopup from '../premium/AnimixPremiumPopup';

const SettingsMenu = ({ visible, videoRef, series, debugInfo, onSpeedChanged, onSrcChanged, onPremium, playbackProvider = 'animix', externalQualities = [], externalQuality = '', onExternalQualityChanged, externalTranslations = [], externalTranslation = null, onExternalTranslationChanged }) => {
    const [quality, setQuality] = useState({ videoQuality: "" });
    const [qualities, setQualities] = useState([]);
    const [speed, setSpeed] = useState(1);
    const [dubbing, setDubbing] = useState('Стандарт');
    const [state, setState] = useState("def");
    const isExternalKodik = playbackProvider === 'kodik';
    const selectedExternalTranslationId = String(externalTranslation?.id ?? '');
    const hasExternalTranslations = externalTranslations.length > 1;

    useEffect(() => {
        // Fetch the list of qualities for the series when the component loads
        const fetchQualities = async () => {
            if (isExternalKodik) {
                const normalizedQualities = externalQualities.map((item) => {
                    const value = item?.quality ?? item;
                    return {
                        privateId: `kodik-${value}`,
                        rawQuality: String(value),
                        videoQuality: `${String(value).replace(/p$/i, '')}p`,
                    };
                });
                setQualities(normalizedQualities);
                setQuality(normalizedQualities.find((item) => item.rawQuality === String(externalQuality)) || normalizedQualities[0] || { videoQuality: '' });
                setDubbing(externalTranslation?.title || 'Стандарт');
                return;
            }
            if (!series) {
                setQualities([]);
                setQuality({ videoQuality: "" });
                return;
            }

            try {
                const response = await ApiService.getQualities(series.animeId, series.seriesNum, series.season);

                if (!response?.data || !Array.isArray(response.data)) {
                    throw new Error('Invalid quality data response');
                }

                const validQualities = response.data.filter(quality =>
                    quality?.privateId && typeof quality.privateId === 'string' &&
                    quality?.videoQuality && typeof quality.videoQuality === 'string'
                );

                setQualities(validQualities);

                const currentQuality = validQualities.find(qualityOption =>
                    qualityOption.privateId === series.privateId
                ) || validQualities.find(qualityOption =>
                    qualityOption.videoQuality === series.videoQuality
                ) || validQualities[0] || { videoQuality: series.videoQuality || "" };

                const savedPrefQuality = localStorage.getItem("savedPrefQuality");
                const matchingQuality = savedPrefQuality
                    ? validQualities.find(qualityOption => qualityOption.videoQuality === savedPrefQuality)
                    : null;

                if (matchingQuality) {
                    setQuality(matchingQuality);
                    if (matchingQuality.privateId !== currentQuality.privateId) {
                        try {
                            const src = await ContentService.fetchVideo(matchingQuality.privateId);
                            if (src && typeof src === 'string') {
                                onSrcChanged(src, matchingQuality.privateId);
                            }
                        } catch (error) {
                            console.error('Quality change failed:', error);
                        }
                    }
                } else {
                    setQuality(currentQuality);
                }
            } catch (error) {
                console.error("Error fetching qualities:", error);
                setQualities([]);
                setQuality({ videoQuality: series.videoQuality || "" });
            }
        };

        setQuality({ videoQuality: isExternalKodik ? `${externalQuality}p` : series?.videoQuality || "" });
        setState("def")
        fetchQualities();
    }, [series, isExternalKodik, externalQualities, externalQuality, externalTranslation]);

    const handleSpeedChange = (newSpeed) => {
        setSpeed(newSpeed);
        onSpeedChanged(newSpeed);
        setState('def');
    };

    const handleQualityChange = async (selectedQuality) => {
        setState("def")
        if (isExternalKodik) {
            setQuality(selectedQuality);
            onExternalQualityChanged(selectedQuality.rawQuality);
            return;
        }
        if (!selectedQuality?.privateId || typeof selectedQuality.privateId !== 'string') {
            console.error('Invalid quality selection:', selectedQuality);
            onPremium();
            return;
        }

        try {
            const src = await ContentService.fetchVideo(selectedQuality.privateId);
            if (!src || typeof src !== 'string') {
                throw new Error('Invalid video source received');
            }

            setQuality(selectedQuality);
            localStorage.setItem("savedPrefQuality", selectedQuality.videoQuality);
            onSrcChanged(src, selectedQuality.privateId);

        } catch (error) {
            console.error('Quality change failed:', error);
            onPremium();
        }
    };

    const handleExternalTranslationChange = async (translation) => {
        if (!translation?.id || !onExternalTranslationChanged || String(translation.id) === selectedExternalTranslationId) {
            setState('def');
            return;
        }

        try {
            await onExternalTranslationChanged(translation.id);
            setDubbing(translation.title || 'Стандарт');
            setState('def');
        } catch (translationError) {
            console.error('Kodik translation change failed:', translationError);
        }
    };


    const getDisplayQualityName = (qualityCode) => {
        switch (qualityCode) {
            case '':
            case null:
            case undefined:
                return 'Авто';
            case 'FULL_HD':
                return '1080p';
            case 'QHD':
                return 'QHD (2K)';
            case 'HD':
                return '720p';
            case 'ULTRA_HD':
                return 'UHD (4K)';
            default:
                return qualityCode; // Fallback to the original code if no match is found
        }
    };
    function getActiveCodec(src) {
        const formats = {
            av01: { patterns: ['av01'], name: 'AV1' },
            hevc: { patterns: ['h265', 'hevc'], name: 'HEVC/H.265' },
            h264: { patterns: ['h264', 'avc'], name: 'H.264' }
        };

        if (!src) return 'Unknown';
        const url = src.toLowerCase();
        const [found] = Object.entries(formats)
            .find(([_, { patterns }]) => patterns.some(p => url.includes(p))) || [];

        return found ? formats[found].name : 'Unknown';
    }

    const menuTitles = {
        def: 'Настройки',
        quality: 'Качество',
        speed: 'Скорость',
        sound: 'Озвучка',
        marks: 'Метки',
        debug: 'Диагностика'
    };

    const renderContent = () => {
        switch (state) {
            case 'def':
                return (<div key="main" className="settings-page">
                    <button type="button" className="settings-option" onClick={() => setState('quality')}>
                        <span className="settings-option-copy">
                            <span className="settings-option-title">Качество</span>
                            <span className="settings-option-caption">Разрешение видео</span>
                        </span>
                        <span className={`option-value ${['ULTRA_HD', 'QHD'].includes(quality.videoQuality) ? 'high' : ''}`}>{getDisplayQualityName(quality.videoQuality)}</span>
                    </button>
                    <button type="button" className="settings-option" onClick={() => setState('speed')}>
                        <span className="settings-option-copy">
                            <span className="settings-option-title">Скорость</span>
                            <span className="settings-option-caption">Темп воспроизведения</span>
                        </span>
                        <span className="option-value">{speed}×</span>
                    </button>
                    {(!isExternalKodik || hasExternalTranslations) && <button type="button" className="settings-option" onClick={() => setState('sound')}>
                        <span className="settings-option-copy">
                            <span className="settings-option-title">Озвучка</span>
                            <span className="settings-option-caption">{isExternalKodik ? 'Озвучка или субтитры' : 'Звуковая дорожка'}</span>
                        </span>
                        <span className="option-value">{dubbing}</span>
                    </button>}
                    {!isExternalKodik && (localStorage.getItem("editor") === "true" || localStorage.getItem("mod") === "true") ? (
                        <button type="button" className="settings-option settings-option-muted" onClick={() => setState('marks')}>
                            <span className="settings-option-title">Редактирование меток</span>
                        </button>
                    ) : ""}
                    <button type="button" className="settings-option settings-option-muted" onClick={() => setState('debug')}>
                        <span className="settings-option-title">Для разработчиков</span>
                    </button>
                </div>);
            case 'speed':
                return (
                    <div key="speed" className="settings-page settings-choice-grid">
                        {[1, 1.25, 1.5, 1.75].map((speedOption) => (
                            <button
                                type="button"
                                className={`settings-choice ${speed === speedOption ? 'is-selected' : ''}`}
                                key={speedOption}
                                onClick={() => handleSpeedChange(speedOption)}
                            >
                                {speedOption}×
                            </button>
                        ))}
                        <div className="settings-option settings-option-note">
                            <p className="settings-text">Чтобы серия была засчитана, смотрите большую часть на 1x</p>
                        </div>
                    </div>
                );
            case 'quality':
                return (
                    <div key="quality" className="settings-page">
                        {qualities.map((qualityOption) => (
                            <button
                                type="button"
                                className={`settings-option ${quality.videoQuality === qualityOption.videoQuality ? 'is-selected' : ''}`}
                                key={qualityOption.privateId}
                                onClick={() => handleQualityChange(qualityOption)}
                            >
                                <span className="settings-option-title">{getDisplayQualityName(qualityOption.videoQuality)}</span>
                                {quality.videoQuality === qualityOption.videoQuality && <span className="settings-check">✓</span>}
                            </button>
                        ))}
                    </div>
                );
            case 'marks':
                return (
                    <>
                        <SkipMarkForm videoRef={videoRef} series={series} />
                    </>
                );
            case 'sound':
                if (isExternalKodik) {
                    return (
                        <div className="settings-page">
                            {externalTranslations.map((translation) => {
                                const selected = String(translation?.id) === selectedExternalTranslationId;
                                return (
                                    <button
                                        type="button"
                                        className={`settings-option ${selected ? 'is-selected' : ''}`}
                                        key={translation.id}
                                        onClick={() => handleExternalTranslationChange(translation)}
                                    >
                                        <span className="settings-option-copy">
                                            <span className="settings-option-title">{translation.title}</span>
                                            <span className="settings-option-caption">{translation.type === 'subtitles' ? 'Субтитры' : 'Озвучка'}</span>
                                        </span>
                                        {selected && <span className="settings-check">✓</span>}
                                    </button>
                                );
                            })}
                        </div>
                    );
                }
                return (
                    <div className="settings-page">
                        <div className="settings-option settings-option-note">
                            <p className="settings-text">Для других озвучек выберите AnimixLite или Kodik RAW в селекторе плеера.</p>
                        </div>
                    </div>
                );
            case 'debug':
          
                const videoElement = videoRef.current;

                // Enhanced codec detection
                const codecSupport = {
                    h264: {
                        mp4: videoElement?.canPlayType('video/mp4; codecs="avc1.42E01E"'),
                        webm: videoElement?.canPlayType('video/webm; codecs="avc1.42E01E"')
                    },
                    h265: {
                        mp4: videoElement?.canPlayType('video/mp4; codecs="hvc1.1.6.L123.00"') ||
                            videoElement?.canPlayType('video/mp4; codecs="hev1.1.6.L123.00"'),
                        webm: videoElement?.canPlayType('video/webm; codecs="hvc1.1.6.L123.00"')
                    },
                    av01: {
                        mp4: videoElement?.canPlayType('video/mp4; codecs="av01.0.05M.08"'),
                        webm: videoElement?.canPlayType('video/webm; codecs="av01.0.05M.08"')
                    }
                };

                const detectedCodecs = [];
                if (codecSupport.h264.mp4 || codecSupport.h264.webm) detectedCodecs.push('H.264');
                if (codecSupport.h265.mp4 || codecSupport.h265.webm) detectedCodecs.push('HEVC/H.265');
                if (codecSupport.av01.mp4 || codecSupport.av01.webm) detectedCodecs.push('AV1');

                const videoStats = {
                    // Player state
                    currentTime: videoElement?.currentTime,
                    duration: videoElement?.duration,
                    buffered: videoElement?.buffered.length > 0 ?
                        `${Math.round(videoElement.buffered.end(0))}s` : 'None',
                    playbackState: {
                        paused: videoElement?.paused,
                        ended: videoElement?.ended,
                        seeking: videoElement?.seeking
                    },
                    networkState: {
                        readyState: videoElement?.readyState,
                        networkState: videoElement?.networkState
                    },
                    error: videoElement?.error?.message || 'None',

                  
                    resolution: videoElement?.videoWidth + 'x' + videoElement?.videoHeight,
                    // Performance metrics
                    performance: {
                        droppedFrames: videoElement?.webkitDroppedFrameCount || 'N/A',
                        decodedFrames: videoElement?.webkitDecodedFrameCount || 'N/A',
                        memoryUsage: performance.memory ?
                            (performance.memory.usedJSHeapSize / 1048576).toFixed(2) + ' MB' : 'N/A'
                    },

                    // Network timing
                    timing: {
                        loadTime: window.performance.timing.loadEventEnd - window.performance.timing.navigationStart,
                        domInteractive: window.performance.timing.domInteractive - window.performance.timing.navigationStart
                    },

                    // Player configuration
                    config: {
                        quality: quality.videoQuality,
                        playbackRate: speed,
                        currentSrc: videoElement?.src?.split('/').pop() || 'Unknown',
                        dubbingTrack: dubbing
                    },

                    // Series context
                    seriesInfo: {
                        animeId: series?.animeId,
                        seriesNum: series?.seriesNum,
                        season: series?.season,
                        episodeTitle: series?.title,
                        debugInfo: debugInfo
                    },

                    // System info
                    environment: {
                        userAgent: navigator.userAgent,
                        platform: navigator.platform,
                        cookiesEnabled: navigator.cookieEnabled,
                        localStorage: {
                            videoQuality: localStorage.getItem("savedPrefQuality"),
                            isEditor: localStorage.getItem("editor"),
                            isMod: localStorage.getItem("mod")
                        }
                    },

                    // Feature detection
                    supportedFeatures: {
                        MSE: 'MediaSource' in window,
                        WebRTC: 'RTCPeerConnection' in window,
                        WebGL: !!document.createElement('canvas').getContext('webgl'),
                        HLS: videoElement?.canPlayType('application/vnd.apple.mpegurl')
                    },

                    // Diagnostics
                    warnings: {
                        qualityIssues: qualities.length === 0 ? 'No qualities available' : 'OK',
                        cors: videoElement?.crossOrigin !== null ? 'Potential CORS issues' : 'CORS configured',
                        autoplay: videoElement?.autoplay ? 'Autoplay enabled' : 'Autoplay blocked'
                    }
                };

                return (
                    <div className="debug-info-container">
                        <div className="debug-section">
                            <h3>Player State</h3>
                            <pre>{JSON.stringify({
                                time: `${Math.round(videoStats.currentTime)}s / ${Math.round(videoStats.duration)}s`,
                                buffer: videoStats.buffered,
                                playbackState: videoStats.playbackState,
                                networkState: videoStats.networkState,
                                resolution: videoStats.resolution
                            }, null, 2)}</pre>
                        </div>


                        <div className="debug-section">
                            <h3>Player Hook</h3>
                            <pre>{JSON.stringify(debugInfo)}</pre>
                        </div>

                        <div className="debug-section">
                            <h3>Performance Metrics</h3>
                            <pre>{JSON.stringify(videoStats.performance, null, 2)}</pre>
                        </div>

                        <div className="debug-section">
                            <h3>Configuration</h3>
                            <pre>{JSON.stringify(videoStats.config, null, 2)}</pre>
                        </div>

                        <div className="debug-section">
                            <h3>Environment</h3>
                            <pre>{JSON.stringify(videoStats.environment, null, 2)}</pre>
                        </div>

                        <div className="debug-section">
                            <h3>Diagnostics</h3>
                            <pre>{JSON.stringify(videoStats.warnings, null, 2)}</pre>
                        </div>

                        <button
                            className="copy-debug-btn"
                            onClick={() => navigator.clipboard.writeText(JSON.stringify(videoStats, null, 2))}
                        >
                            Копировать информацию для задротов
                        </button>
                    </div>
                );
        }
    }

    return (
        <div className={`settings-menu ${!visible && state !== 'debug' ? 'hidden' : ''}`}>
            <div className="settings-menu-header">
                {state !== 'def' && (
                    <button type="button" className="settings-back-button" onClick={() => setState('def')} aria-label="Назад">
                        ←
                    </button>
                )}
                <div className="settings-menu-heading">
                    <h3>{menuTitles[state] || 'Настройки'}</h3>
                </div>
            </div>
            <div className="settings-menu-content">
                {renderContent()}
            </div>
        </div>
    );
};

export default SettingsMenu;
